import { useNavigate } from 'react-router-dom';
import { Box, Chip, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { EmptyState } from '../../components/EmptyState';
import { CounselorDashboard } from '../../types/dashboard';

const READY_STATUSES = new Set(['READY_TO_FLY', 'DEPARTED', 'ARRIVED']);

interface MyRecentApplicationsCardProps {
  data?: CounselorDashboard['recentApplications'];
  loading?: boolean;
}

/** Counselor Dashboard's "My Recent Applications" / "My Latest Admissions"
 * — only this Counselor's own students' applications (spec §8), sourced
 * from GET /dashboard/counselor rather than the general, branch-only
 * /applications listing. */
export const MyRecentApplicationsCard = ({ data, loading }: MyRecentApplicationsCardProps) => {
  const navigate = useNavigate();
  const applications = data ?? [];

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">My Recent Applications</Typography>
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate('/admissions')}
        >
          View all
        </Typography>
      </Stack>
      {applications.length === 0 ? (
        <EmptyState loading={loading} message="No applications yet" />
      ) : (
        <List dense disablePadding>
          {applications.map((app) => (
            <ListItemButton key={app.id} onClick={() => navigate('/admissions')} sx={{ borderRadius: 2, px: 1 }}>
              <ListItemText
                primary={`${app.student.firstName} ${app.student.lastName}`}
                secondary={`${app.applicationNumber} · ${app.university.name}`}
              />
              <Box>
                <Chip
                  size="small"
                  label={app.status.replace(/_/g, ' ')}
                  color={READY_STATUSES.has(app.status) ? 'success' : 'default'}
                />
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};
